// DashboardPanel.jsx — shared panel shell for all dashboards
// Usage: <DashboardPanel title="Foo" icon="📊" onViewAll={...}>...</DashboardPanel>
import React from "react";

const NEON = "#00FF99";

export default function DashboardPanel({
  title,
  icon,
  subtitle,
  onViewAll,
  viewAllLabel = "View all",
  loading = false,
  empty = false,
  emptyMessage = "No data yet",
  accent = NEON,           // override accent colour per panel if needed
  className = "",
  children,
  headerRight,             // optional custom element in the header right (e.g. a filter)
}) {
  return (
    <div
      className={`rounded-xl border bg-black/60 backdrop-blur-sm transition hover:border-opacity-60 ${className}`}
      style={{
        borderColor: `${accent}33`,   // 20% opacity
        boxShadow: `0 0 0 1px ${accent}08 inset`,
      }}
    >
      {/* ── Header ─────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: `${accent}1a` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {icon && (
            <span className="text-base flex-shrink-0" aria-hidden>
              {icon}
            </span>
          )}
          <div className="min-w-0">
            <h3
              className="text-xs font-semibold uppercase tracking-widest truncate"
              style={{ color: accent, letterSpacing: "0.1em" }}
            >
              {title}
            </h3>
            {subtitle && (
              <p className="text-[10px] text-white/40 truncate">{subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {headerRight}
          {onViewAll && (
            <button
              onClick={onViewAll}
              className="text-[10px] font-medium px-2 py-1 rounded-md transition"
              style={{
                color: accent,
                border: `1px solid ${accent}33`,
                background: "transparent",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = `${accent}10`)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {viewAllLabel} →
            </button>
          )}
        </div>
      </div>

      {/* ── Body ───────────────────────────────────── */}
      <div className="p-4">
        {loading ? (
          <PanelSkeleton accent={accent} />
        ) : empty ? (
          <PanelEmpty message={emptyMessage} />
        ) : (
          children
        )}
      </div>
    </div>
  );
}

/* ─── Loading skeleton ─── */
function PanelSkeleton({ accent }) {
  return (
    <div className="space-y-2 animate-pulse">
      <div
        className="h-3 rounded"
        style={{ background: `${accent}15`, width: "80%" }}
      />
      <div
        className="h-3 rounded"
        style={{ background: `${accent}10`, width: "60%" }}
      />
      <div
        className="h-3 rounded"
        style={{ background: `${accent}15`, width: "70%" }}
      />
    </div>
  );
}

/* ─── Empty state ─── */
function PanelEmpty({ message }) {
  return (
    <div className="text-center py-4">
      <p className="text-xs text-white/40">{message}</p>
    </div>
  );
}

/* ─── Sub-components for common panel patterns ─── */

// Big KPI number: <PanelStat value="248" label="Headcount" trend="+6 this month" />
export function PanelStat({ value, label, trend, trendUp, accent = NEON }) {
  const trendColor = trendUp == null ? "#9ca3af" : trendUp ? accent : "#f87171";
  return (
    <div className="flex flex-col">
      <p
        className="text-3xl font-bold leading-none"
        style={{ color: accent }}
      >
        {value}
      </p>
      {label && (
        <p className="text-[11px] text-white/50 mt-1 uppercase tracking-wide">
          {label}
        </p>
      )}
      {trend && (
        <p className="text-[10px] mt-1" style={{ color: trendColor }}>
          {trendUp === true ? "↑ " : trendUp === false ? "↓ " : ""}
          {trend}
        </p>
      )}
    </div>
  );
}

// Simple list row: <PanelRow title="..." subtitle="..." right="3 days"/>
export function PanelRow({ title, subtitle, right, onClick, accent = NEON }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center justify-between gap-3 py-2 border-b last:border-b-0 ${
        onClick ? "cursor-pointer hover:bg-white/5 -mx-2 px-2 rounded" : ""
      }`}
      style={{ borderColor: "rgba(255,255,255,0.05)" }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white truncate">{title}</p>
        {subtitle && (
          <p className="text-[11px] text-white/45 truncate">{subtitle}</p>
        )}
      </div>
      {right && (
        <span
          className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0"
          style={{
            color: accent,
            background: `${accent}1a`,
            border: `1px solid ${accent}33`,
          }}
        >
          {right}
        </span>
      )}
    </div>
  );
}

// Horizontal bar meter: <PanelBar label="Engineering" value={48} max={100} />
export function PanelBar({ label, value, max = 100, suffix = "%", accent = NEON }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-white/70">{label}</span>
        <span className="text-xs font-medium" style={{ color: accent }}>
          {value}
          {suffix}
        </span>
      </div>
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.06)" }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: accent,
            boxShadow: `0 0 8px ${accent}66`,
          }}
        />
      </div>
    </div>
  );
}

// Quick action button: <PanelAction icon="📝" label="Apply leave" onClick={...}/>
export function PanelAction({ icon, label, onClick, accent = NEON }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1 p-3 rounded-lg border transition w-full"
      style={{
        borderColor: `${accent}33`,
        background: "rgba(0,0,0,0.3)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.background = `${accent}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = `${accent}33`;
        e.currentTarget.style.background = "rgba(0,0,0,0.3)";
      }}
    >
      <span className="text-lg" aria-hidden>{icon}</span>
      <span className="text-[11px] text-white/80 text-center leading-tight">{label}</span>
    </button>
  );
}